// src/components/AppointmentForm.tsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Error403Page from "./Error_backend";
import {
  Appointment_form,
  AppointmentFormData,
  AppointmentStatus,
  Service,
  Patient,
  Office,
} from "../interfaces/types";
import api from "../api.js";
import { useAppointmentForm } from "../hooks/useAppointmentForm";
import "../style/css/AppointmentForm.css";

// Props for the component
interface AppointmentFormProps {
  placeholderPatient?: Appointment_form["patient"];
}

// Combined data interface for the single API call
interface FormDataResponse {
  services: Service[];
  patients: Patient[];
  offices: Office[];
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  placeholderPatient,
}) => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [initialAppointment, setInitialAppointment] = useState<
    Appointment_form | undefined
  >(undefined);
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [patientSearch, setPatientSearch] = useState("");
  const [showPatientResults, setShowPatientResults] = useState(false);
  const [loadingFormData, setLoadingFormData] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [intervalNum, setIntervalNum] = useState<number>(1);
  const [intervalUnit, setIntervalUnit] = useState<string>("weeks");
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [recurrenceSummary, setRecurrenceSummary] = useState<string>("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [untilDate, setUntilDate] = useState<string>("");

  const patientSearchRef = useRef<HTMLDivElement>(null);

  // API base URL
  const API_BASE = "/appointments";
  const FORM_DATA_API = "/appointments/form_data"; // Single endpoint for all form data

  // Get user's timezone with fallback
  const getUserTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch (error) {
      return "UTC";
    }
  };

  // Calculate next hour from current time
  const getNextHour = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    now.setMinutes(0);
    now.setSeconds(0);
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  // Get current date in YYYY-MM-DD format
  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  // Calculate minimum time based on selected date
  const getMinTimeForDate = (date: string) => {
    const today = getTodayDate();
    if (date === today) {
      // For today, require at least 1 hour from now
      const now = new Date();
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
      return now.toTimeString().slice(0, 5);
    }
    return "00:00";
  };

  // Validate form data
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Date validation
    const today = getTodayDate();

    if (!formData.date) {
      errors.date = "La fecha es requerida";
    } else if (formData.date < today) {
      errors.date = "No se pueden crear citas en fechas pasadas";
    } else if (formData.date === today) {
      // If date is today, validate time
      if (!formData.time) {
        errors.time = "La hora de inicio es requerida";
      } else {
        // Get current time plus 1 hour
        const now = new Date();
        now.setHours(now.getHours() + 1);
        const minHour = now.getHours();
        const minMinute = now.getMinutes();

        const [selectedHour, selectedMinute] = formData.time
          .split(":")
          .map(Number);

        // Create date objects for comparison (using same date)
        const minTime = new Date();
        minTime.setHours(minHour, minMinute, 0, 0);

        const selectedTime = new Date();
        selectedTime.setHours(selectedHour, selectedMinute, 0, 0);

        if (selectedTime < minTime) {
          errors.time =
            "Para citas hoy, la hora debe ser al menos 1 hora en adelante";
        }
      }
    }

    // Time validation - end time must be after start time
    if (formData.time && formData.end_time) {
      const [startHour, startMinute] = formData.time.split(":").map(Number);
      const [endHour, endMinute] = formData.end_time.split(":").map(Number);

      if (
        endHour < startHour ||
        (endHour === startHour && endMinute <= startMinute)
      ) {
        errors.end_time =
          "La hora de fin debe ser posterior a la hora de inicio";
      }
    }

    // Required fields
    if (!formData.patient) {
      errors.patient = "Seleccione un paciente";
    }
    if (!formData.service || formData.service.id === "empty") {
      errors.service = "Seleccione un servicio";
    }
    if (!formData.office || formData.office === "empty") {
      errors.office = "Seleccione un consultorio";
    }
    if (!formData.time) {
      errors.time = "La hora de inicio es requerida";
    }
    if (!formData.end_time) {
      errors.end_time = "La hora de fin es requerida";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        patientSearchRef.current &&
        !patientSearchRef.current.contains(event.target as Node)
      ) {
        setShowPatientResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Single API call for all form data
  useEffect(() => {
    const fetchFormData = async () => {
      try {
        setLoadingFormData(true);
        const response = await api.get<FormDataResponse>(
          `${FORM_DATA_API}/?id=${id}`,
        );

        if (response && response.data) {
          setServices(response.data.services);
          setPatients(response.data.patients);
          setFilteredPatients(response.data.patients);
          setOffices(response.data.offices);
        }
      } catch (err) {
        setError("Error al cargar los datos del formulario");
      } finally {
        setLoadingFormData(false);
      }
    };

    fetchFormData();
  }, []);

  const toggleDay = (dayValue: string) => {
    setSelectedDays((prev) => {
      if (prev.includes(dayValue)) {
        return prev.filter((d) => d !== dayValue);
      } else {
        return [...prev, dayValue];
      }
    });
  };

  useEffect(() => {
    let text = "";

    // Format day list
    let daysString = "";
    if (selectedDays.length === 0) {
      daysString = "";
    } else if (selectedDays.length === 1) {
      daysString = "el " + selectedDays[0];
    } else {
      const daysCopy = [...selectedDays];
      const lastDay = daysCopy.pop();
      daysString = "los " + daysCopy.join(", ") + " y " + lastDay;
    }

    // Format end date or use "indefinitely" - FIXED TIMEZONE ISSUE
    let endDateText = "de forma indefinida";
    if (untilDate) {
      // Parse the date without timezone conversion
      const [year, month, day] = untilDate.split("-");
      endDateText = `hasta el ${day}/${month}/${year}`;
    }

    if (intervalUnit === "days") {
      if (intervalNum === 1)
        text = `Tu cita será <strong>todos los días</strong>, ${endDateText}.`;
      else
        text = `Tu cita será <strong>cada ${intervalNum} días</strong>, ${endDateText}.`;
    } else if (intervalUnit === "weeks") {
      let frequency =
        intervalNum === 1 ? "cada semana" : `cada ${intervalNum} semanas`;

      if (selectedDays.length === 0) {
        text =
          "<span style='color:var(--error)'>Por favor selecciona qué días de la semana.</span>";
      } else {
        text = `Tu cita será <strong>${daysString}</strong>, ${frequency}, ${endDateText}.`;
      }
    } else if (intervalUnit === "months") {
      if (intervalNum === 1)
        text = `Tu cita será <strong>mensual</strong> (el mismo día del mes), ${endDateText}.`;
      else
        text = `Tu cita será <strong>cada ${intervalNum} meses</strong>, ${endDateText}.`;
    }

    setRecurrenceSummary(text);
  }, [intervalNum, intervalUnit, selectedDays, untilDate]);

  // Filter patients based on search
  useEffect(() => {
    if (patientSearch.trim() === "") {
      setFilteredPatients(patients);
    } else {
      const filtered = patients.filter((patient) =>
        patient.name.toLowerCase().includes(patientSearch.toLowerCase()),
      );
      setFilteredPatients(filtered);
    }
  }, [patientSearch, patients]);

  // Fetch appointment data if in edit mode
  useEffect(() => {
    const fetchAppointment = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`${API_BASE}/${id}/`);
        if (!response) {
          throw new Error("Failed to fetch appointment");
        }
        const appointmentData = await response.data;
        setInitialAppointment(appointmentData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchAppointment();
  }, [id]);

  const { formData, handleChange, patient, setFormData } =
    useAppointmentForm(initialAppointment);

  const titleText = isEditMode ? "Editar Cita" : "Crear Nueva Cita";

  // Handle service change and auto-calculate end time
  const handleServiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const serviceId = e.target.value;

    setFormData((prev: AppointmentFormData) => {
      const selectedService = services.find(
        (service) => service.id === serviceId,
      );

      // Create updated object with proper typing
      const updated: AppointmentFormData = {
        ...prev,
        service: selectedService || prev.service, // Keep existing service if not found
      };

      // If we found a valid service, recalculate times
      if (selectedService && selectedService.duration_minutes) {
        // Calculate end time based on start time and service duration
        let startTime = updated.time;

        // If no start time is set, use appropriate time
        if (!startTime) {
          const today = getTodayDate();
          if (updated.date === today) {
            startTime = getCurrentTime();
          } else {
            startTime = getNextHour();
          }
          updated.time = startTime;
        }

        if (startTime) {
          const [hours, minutes] = startTime.split(":").map(Number);
          const startDate = new Date();
          startDate.setHours(hours, minutes, 0, 0);

          const endDate = new Date(
            startDate.getTime() + selectedService.duration_minutes * 60000,
          );
          const endHours = String(endDate.getHours()).padStart(2, "0");
          const endMinutes = String(endDate.getMinutes()).padStart(2, "0");
          const endTime = `${endHours}:${endMinutes}`;

          updated.end_time = endTime;
          updated.duration = `${selectedService.duration_minutes}min`;
        }
      }

      return updated;
    });
  };

  // Handle start time change and recalculate end time if service is selected
  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const startTime = e.target.value;

    setFormData((prev) => {
      const updated = { ...prev, time: startTime };

      // Recalculate end time if a service is selected
      if (updated.service && updated.service.id !== "empty") {
        const selectedService = services.find(
          (service) => service.id === updated.service.id,
        );

        if (selectedService && selectedService.duration_minutes && startTime) {
          const [hours, minutes] = startTime.split(":").map(Number);
          const startDate = new Date();
          startDate.setHours(hours, minutes, 0, 0);

          const endDate = new Date(
            startDate.getTime() + selectedService.duration_minutes * 60000,
          );
          const endHours = String(endDate.getHours()).padStart(2, "0");
          const endMinutes = String(endDate.getMinutes()).padStart(2, "0");
          const endTime = `${endHours}:${endMinutes}`;

          updated.end_time = endTime;
        }
      }

      return updated;
    });
  };

  // Handle patient search and selection
  const handlePatientSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPatientSearch(e.target.value);
    setShowPatientResults(true);
  };

  const handlePatientSelect = (selectedPatient: Patient) => {
    setFormData((prev) => ({
      ...prev,
      patient: selectedPatient,
    }));
    setPatientSearch(selectedPatient.name);
    setShowPatientResults(false);
  };

  const handleApiError = (err: any): string => {
    // Check if it's a network error
    if (!err.response) {
      return "Error de conexión. Por favor, verifique su conexión a internet.";
    }

    const { status, data } = err.response;

    // Handle different error statuses
    switch (status) {
      case 400:
      case 409:
        // Bad Request or Conflict - show backend error message
        return data.error || data.details || "Error en los datos enviados";

      case 401:
        return "No autorizado. Por favor, inicie sesión nuevamente.";

      case 403:
        return "No tiene permisos para realizar esta acción.";

      case 404:
        return "Recurso no encontrado.";

      case 500:
        return (
          data.error ||
          "Error interno del servidor. Por favor, intente más tarde."
        );

      default:
        return (
          data.error ||
          "Ha ocurrido un error inesperado. Por favor, intente nuevamente."
        );
    }
  };

  // Navigation handlers for buttons
  const handleNavigateToServices = () => {
    navigate("/services");
  };

  const handleNavigateToOffices = () => {
    navigate("/offices");
  };

  // Enhanced submit handler with better error handling
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setError("Por favor, corrija los errores en el formulario");
      return;
    }

    if (!patient) {
      setError("Patient data is required");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const userTimezone = getUserTimezone();

      const dataToSave = {
        ...formData,
        patient: patient,
        timezone: userTimezone,
        recurrence: isRecurring
          ? {
              interval_num: intervalNum,
              interval_unit: intervalUnit,
              days: selectedDays,
              until_date: untilDate || null,
              timezone: userTimezone,
            }
          : null,
      };

      let response;
      if (isEditMode) {
        response = await api.put(`${API_BASE}/${id}/`, dataToSave);
      } else {
        response = await api.post(`${API_BASE}/`, dataToSave);
      }

      if (!response) {
        throw new Error(
          `Failed to ${isEditMode ? "update" : "create"} appointment`,
        );
      }

      // Check if response indicates success
      if (response.data && response.data.success === false) {
        throw new Error(response.data.error || "Operation failed");
      }

      // Show success message before navigating
      setError(null);

      // Optional: Show success message
      const successMessage = isEditMode
        ? "Cita actualizada exitosamente"
        : "Cita creada exitosamente";

      // You could add a success state here and show a temporary message
      setTimeout(() => {
        navigate("/appointments");
      }, 1000);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!isEditMode || !formData.id) {
      return;
    }

    if (!window.confirm("¿Estás seguro de que quieres eliminar esta cita?")) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      const response = await api.delete(`${API_BASE}/${formData.id}/`);

      if (!response) {
        throw new Error("Failed to delete appointment");
      }

      navigate("/appointments");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setDeleting(false);
    }
  };

  const handleBackClick = () => {
    navigate("/appointments");
  };

  // Update time when date changes
  useEffect(() => {
    if (!formData.date || isEditMode) return;

    const today = getTodayDate();
    if (formData.date === today && !formData.time) {
      // If selecting today's date, set time to 1 hour from now
      setFormData((prev) => ({
        ...prev,
        time: getCurrentTime(),
      }));
    } else if (!formData.time) {
      // For future dates, set to next hour by default
      setFormData((prev) => ({
        ...prev,
        time: getNextHour(),
      }));
    }
  }, [formData.date, isEditMode]);

  // Show loading state
  if (loading) {
    return (
      <div className="form-entire-container-form-appointment">
        <div className="form-container-form-appointment">
          <div className="loading-message-form-appointment">
            <i className="fas fa-spinner fa-spin"></i> Cargando cita...
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && isEditMode) {
    return (
      <Error403Page
        code={500}
        title="Recurso no disponible."
        message="Hemos tenido problemas para acceder al recurso."
        homeHref="/"
        loginHref="/login"
        supportHref="/support"
        onHomeClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
        onLoginClick={(e) => {
          e.preventDefault();
          navigate("/login");
        }}
      />
    );
  }

  return (
    <div className="form-entire-container-form-appointment">
      <div className="form-container-form-appointment">
        <div className="header-actions-form-appointment">
          <button
            className="back-link-form-appointment"
            onClick={handleBackClick}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <i className="fas fa-arrow-left"></i> Volver a Citas
          </button>
          <h2 style={{ margin: 0, fontSize: "1.5rem" }}>
            <i
              className="fas fa-edit"
              style={{ color: "var(--color-accent)" }}
            ></i>{" "}
            {titleText}
          </h2>
          <span></span>
        </div>

        <div className="form-card-form-appointment">
          <h3 style={{ marginBottom: "1.5rem", textAlign: "center" }}>
            {initialAppointment ? initialAppointment.title : "Nueva cita"}
          </h3>

          {error && (
            <div className="message-box-form-appointment message-error-form-appointment">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Patient Search and Selection Group */}
            <div className="form-group-form-appointment">
              <label htmlFor="patient-search">Paciente</label>
              <div className="search-container" ref={patientSearchRef}>
                <input
                  type="text"
                  id="patient-search"
                  value={patientSearch}
                  onChange={handlePatientSearchChange}
                  onFocus={() => setShowPatientResults(true)}
                  placeholder="Buscar paciente por nombre..."
                  disabled={saving || deleting || loadingFormData}
                  className={formErrors.patient ? "input-error" : ""}
                />
                {formErrors.patient && (
                  <span className="field-error">{formErrors.patient}</span>
                )}

                {showPatientResults &&
                  patientSearch &&
                  filteredPatients.length > 0 && (
                    <div className="search-results">
                      {filteredPatients.slice(0, 5).map((patient) => (
                        <div
                          key={patient.id}
                          className="search-result"
                          onClick={() => handlePatientSelect(patient)}
                        >
                          <div className="result-name">{patient.name}</div>
                          {patient.email && (
                            <div className="result-detail">{patient.email}</div>
                          )}
                          {patient.phone && (
                            <div className="result-detail">{patient.phone}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                {formData.patient && (
                  <div className="selected-item">
                    <strong>Paciente seleccionado:</strong>{" "}
                    {formData.patient.name}
                  </div>
                )}
              </div>
            </div>

            {/* Service Selection with Add Button */}
            <div className="form-group-form-appointment">
              <label htmlFor="service">Servicio/Motivo de la Cita</label>
              <div className="select-with-button">
                <select
                  id="service"
                  name="service"
                  value={formData.service?.id}
                  onChange={handleServiceChange}
                  required
                  disabled={saving || deleting || loadingFormData}
                  className={formErrors.service ? "input-error" : ""}
                >
                  <option value="empty">Selecciona una opción</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name} ({service.duration_minutes} min)
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-icon"
                  onClick={handleNavigateToServices}
                  title="Ir a gestión de servicios"
                  disabled={saving || deleting}
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
              {formErrors.service && (
                <span className="field-error">{formErrors.service}</span>
              )}
            </div>

            {/* Office Selection with Add Button */}
            <div className="form-group-form-appointment">
              <label htmlFor="office">Consultorio</label>
              <div className="select-with-button">
                <select
                  id="office"
                  name="office"
                  value={formData.office || "empty"}
                  onChange={handleChange}
                  required
                  disabled={saving || deleting || loadingFormData}
                  className={formErrors.office ? "input-error" : ""}
                >
                  <option value="empty">Selecciona un consultorio</option>
                  {offices.map((office) => (
                    <option key={office.id} value={office.id}>
                      {office.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-icon"
                  onClick={handleNavigateToOffices}
                  title="Ir a gestión de consultorios"
                  disabled={saving || deleting}
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
              {formErrors.office && (
                <span className="field-error">{formErrors.office}</span>
              )}
            </div>

            <div className="grid-three-form-appointment">
              <div className="form-group-form-appointment">
                <label htmlFor="date">Fecha de la Cita</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  disabled={saving || deleting}
                  min={getTodayDate()} // Prevent past dates
                  className={formErrors.date ? "input-error" : ""}
                />
                {formErrors.date && (
                  <span className="field-error">{formErrors.date}</span>
                )}
              </div>

              <div className="form-group-form-appointment">
                <label htmlFor="time">Hora de Inicio</label>
                <input
                  type="time"
                  id="time"
                  name="time"
                  value={formData.time}
                  onChange={handleStartTimeChange}
                  required
                  disabled={saving || deleting}
                  min={getMinTimeForDate(formData.date)}
                  className={formErrors.time ? "input-error" : ""}
                />
                {formErrors.time && (
                  <span className="field-error">{formErrors.time}</span>
                )}
              </div>

              <div className="form-group-form-appointment">
                <label htmlFor="end_time">Hora de Fin</label>
                <input
                  type="time"
                  id="end_time"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleChange}
                  required
                  disabled={saving || deleting}
                  className={formErrors.end_time ? "input-error" : ""}
                />
                {formErrors.end_time && (
                  <span className="field-error">{formErrors.end_time}</span>
                )}
              </div>
            </div>
            <div
              className="form-group-form-appointment"
              style={{ marginTop: "-0.5rem", marginBottom: "1rem" }}
            >
              <small
                style={{ color: "var(--neutral-500)", fontStyle: "italic" }}
              >
                <i
                  className="fas fa-clock"
                  style={{ marginRight: "0.5rem" }}
                ></i>
                Zona horaria: {getUserTimezone()}
              </small>
              {formData.date === getTodayDate() && (
                <small
                  style={{
                    color: "var(--color-accent)",
                    display: "block",
                    marginTop: "0.25rem",
                  }}
                >
                  <i
                    className="fas fa-info-circle"
                    style={{ marginRight: "0.5rem" }}
                  ></i>
                  Para hoy, la hora mínima es 1 hora en adelante
                </small>
              )}
            </div>

            {/* Duration field - only shown in edit mode and read-only */}
            {isEditMode && (
              <div className="form-group-form-appointment">
                <label htmlFor="duration">Duración</label>
                <input
                  type="text"
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  readOnly
                  style={{
                    backgroundColor: "var(--neutral-100)",
                    cursor: "default",
                  }}
                />
              </div>
            )}

            <div
              className="recurrence-toggle-wrapper"
              onClick={() => setIsRecurring(!isRecurring)}
            >
              <input
                type="checkbox"
                className="recurrence-checkbox"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
                // Evita doble evento al hacer click en el div padre
                onClick={(e) => e.stopPropagation()}
              />
              <span className="recurrence-toggle-label">
                <i
                  className="fas fa-sync-alt"
                  style={{ marginRight: "8px" }}
                ></i>
                Repetir esta cita
              </span>
            </div>

            {/* Renderizado Condicional: Solo mostramos la tarjeta si isRecurring es true */}
            {isRecurring && (
              <div className="recurrency-form-card fade-in-enter-active">
                <h4
                  style={{ color: "var(--color-accent)", marginBottom: "1rem" }}
                >
                  Configuración
                </h4>

                <div className="recurrency-form-group">
                  <label className="recurrency-form-label">Repetir cada:</label>

                  <div className="recurrency-form-row">
                    <input
                      type="number"
                      value={intervalNum}
                      onChange={(e) => setIntervalNum(Number(e.target.value))}
                      min="1"
                      className="recurrency-form-input"
                      style={{ width: "80px" }}
                    />

                    <select
                      value={intervalUnit}
                      onChange={(e) => setIntervalUnit(e.target.value)}
                      className="recurrency-form-select"
                    >
                      {/* <option value="days">Día(s)</option> */}
                      <option value="weeks">Semana(s)</option>
                      {/* <option value="months">Mes(es)</option> */}
                    </select>
                  </div>
                </div>

                {/* Sección de días (Igual que antes) */}
                <div
                  className="recurrency-form-group"
                  style={{
                    opacity: intervalUnit === "weeks" ? 1 : 0.5,
                    pointerEvents: intervalUnit === "weeks" ? "auto" : "none",
                    transition: "opacity 0.3s",
                  }}
                >
                  <label className="recurrency-form-label">
                    En estos días:
                  </label>
                  <div className="recurrency-form-week-wrapper">
                    {[
                      "Lunes",
                      "Martes",
                      "Miércoles",
                      "Jueves",
                      "Viernes",
                      "Sábado",
                      "Domingo",
                    ].map((day, index) => (
                      <label key={index}>
                        <input
                          type="checkbox"
                          value={day}
                          checked={selectedDays.includes(day)}
                          onChange={() => toggleDay(day)}
                        />
                        <span className="recurrency-form-day-circle">
                          {day.charAt(0)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div
                  className="recurrency-form-group"
                  style={{ marginTop: "1.5rem" }}
                >
                  <label
                    className="recurrency-form-label"
                    htmlFor="untilDateInput"
                  >
                    Repetir hasta (Fecha de Fin):
                  </label>
                  <input
                    type="date"
                    id="untilDateInput"
                    className="recurrency-form-input"
                    value={untilDate}
                    onChange={(e) => setUntilDate(e.target.value)}
                    // Aseguramos que la fecha de fin no sea anterior a la fecha de inicio de la cita
                    min={formData.date || getTodayDate()}
                    style={{ width: "100%" }}
                  />
                  <small
                    style={{
                      color: "var(--neutral-500)",
                      display: "block",
                      marginTop: "4px",
                    }}
                  >
                    Dejar vacío para repetición indefinida.
                  </small>
                </div>

                <div
                  className="recurrency-form-summary"
                  dangerouslySetInnerHTML={{ __html: recurrenceSummary }}
                ></div>
              </div>
            )}

            <div className="form-group-form-appointment">
              <label htmlFor="notes">Notas de la Última Sesión</label>
              <textarea
                id="notes"
                name="notes"
                rows={3}
                placeholder="Ej: Dolor lumbar, primera sesión..."
                value={formData.notes}
                onChange={handleChange}
                disabled={saving || deleting}
              ></textarea>
            </div>

            <hr style={{ borderColor: "#e5e7eb", margin: "16px 0" }} />

            <div className="action-footer-form-appointment">
              {isEditMode && (
                <button
                  type="button"
                  className="btn--delete-form-appointment"
                  onClick={handleDeleteClick}
                  disabled={saving || deleting}
                >
                  <i className="fas fa-trash-alt"></i>
                  {deleting ? "Eliminando..." : "Eliminar Cita"}
                </button>
              )}
              <button
                type="submit"
                className={`btn btn--primary btn--large btn--save-form-appointment ${
                  !isEditMode ? "btn--full-width-in-create" : ""
                }`}
                disabled={saving || deleting}
              >
                <i className="fas fa-sync-alt"></i>{" "}
                {saving
                  ? isEditMode
                    ? "Guardando..."
                    : "Creando..."
                  : isEditMode
                    ? "Guardar Cambios"
                    : "Crear Cita"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppointmentForm;
